import React from 'react';
import { PageHeader, PageFooter } from './PageDecoration';
import { Coins, Wallet, ShoppingBag, Stats, Heart, Shield, Target } from 'react-flaticons';

const DiagnosisPage = ({ data, calculations, name, pageNum = 4 }) => {
    const m = calculations.metrics || {};
    const metrics = m; // Backward compatibility
    const grades = calculations.grades || {};
    
    const diagnosisData = calculations.diagnosis || {};
    const isCouple = data?.personalInfo?.familyType === 'couple';
    const jointSalary = data?.jointSalary || {};

    const safeParse = (v) => { 
        if (typeof v === "number") return v; 
        if (!v) return 0; 
        return parseFloat(v.toString().replace(/[₹, \s]/g, "")) || 0; 
    };

    const fmtK = (val) => {
        if (!val) return "0";
        if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
        if (val >= 1000) return `${Math.round(val / 1000)}k`;
        return val.toString();
    };

    const fmtL = (val) => {
        if (!val) return "0";
        if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
        if (val >= 100000) return `${(val / 100000).toFixed(2)}L`;
        if (val >= 1000) return `${Math.round(val / 1000)}k`;
        return val.toString();
    };

    const fmt = v => v ? Math.round(v).toLocaleString('en-IN') : '0';

    let srVal = 0, eePct = 0, lsPct = 0, dtiVal = 0, emActual = 0, emTarget = 0, lifeActual = 0, lifeTarget = 0;
    let investments = 0, calcEssential = 0, lifestyleSpending = 0, totalEMIs = 0, efPctFunded = 0;
    let combinedMonthly = 0;

    if (isCouple) {
        const sectionB = jointSalary.sectionB || {};
        const sectionC = jointSalary.sectionC || {};
        const sectionD = jointSalary.sectionD || {};
        const sectionE = jointSalary.sectionE || {};
        const sectionF = jointSalary.sectionF || {};
        const sectionG = jointSalary.sectionG || {};

        const hSalary = safeParse(sectionB.husbandSalary) || 0;
        const wSalary = safeParse(sectionB.wifeSalary) || 0;
        const hOther = safeParse(sectionB.husbandOtherIncome) || 0;
        const wOther = safeParse(sectionB.wifeOtherIncome) || 0;

        const hTotal = hSalary + hOther;
        const wTotal = wSalary + wOther;
        combinedMonthly = hTotal + wTotal;
        const combinedAnnual = combinedMonthly * 12;

        const hl = safeParse(sectionF.homeLoanEMI) || safeParse(sectionC.homeLoanEMI) || 0;
        const pl = safeParse(sectionF.personalLoanEMI) || safeParse(sectionC.personalLoanEMI) || 0;
        const cl = safeParse(sectionF.carLoanEMI) || safeParse(sectionC.carLoanEMI) || 0;

        const groceriesUtilities = (safeParse(sectionC.groceries) || 0) + (safeParse(sectionC.rent) || 0) + (safeParse(sectionC.schoolFees) || 0) + (safeParse(sectionC.otherExpenses) || 0);
        const transport = (safeParse(sectionC.transport) || 0) + cl;
        const dining = safeParse(sectionC.dining) || 0;
        const shopping = safeParse(sectionC.shopping) || 0;

        const insurance = (safeParse(sectionC.healthInsurance) || 0) + 
                          (safeParse(sectionE.husbandLifePremium) || 0) + 
                          (safeParse(sectionE.wifeLifePremium) || 0) + 
                          (safeParse(sectionE.husbandHealthPremium) || 0) + 
                          (safeParse(sectionE.wifeHealthPremium) || 0);

        investments = (safeParse(sectionD.husbandEPF) || 0) + (safeParse(sectionD.wifeEPF) || 0) +
                      (safeParse(sectionD.husbandPPF) || 0) + (safeParse(sectionD.wifePPF) || 0) +
                      (safeParse(sectionD.husbandMutualFund) || 0) + (safeParse(sectionD.wifeMutualFund) || 0) +
                      (safeParse(sectionD.husbandELSS) || 0) + (safeParse(sectionD.wifeELSS) || 0) +
                      (safeParse(sectionD.husbandNPS) || 0) + (safeParse(sectionD.wifeNPS) || 0) +
                      (safeParse(sectionD.husbandFD) || 0) + (safeParse(sectionD.wifeFD) || 0) +
                      (safeParse(sectionD.husbandOtherInvest) || 0) + (safeParse(sectionD.wifeOtherInvest) || 0);

        srVal = combinedMonthly > 0 ? (investments / combinedMonthly) * 100 : 0;
        calcEssential = groceriesUtilities + (safeParse(sectionC.transport) || 0) + insurance + hl;
        eePct = combinedMonthly > 0 ? (calcEssential / combinedMonthly) * 100 : 0;
        lifestyleSpending = dining + shopping;
        lsPct = combinedMonthly > 0 ? (lifestyleSpending / combinedMonthly) * 100 : 0;
        totalEMIs = hl + pl + cl;
        dtiVal = combinedMonthly > 0 ? (totalEMIs / combinedMonthly) * 100 : 0;

        emActual = safeParse(sectionG.emergencyFund) || 0;
        const jointExpenses = groceriesUtilities + (safeParse(sectionC.transport) || 0) + dining + shopping + insurance + hl + pl + cl;
        const monthlyExpenses = jointExpenses || (combinedMonthly * 0.5) || 50000;
        emTarget = monthlyExpenses * 6;
        efPctFunded = emTarget > 0 ? (emActual / emTarget) * 100 : 0;

        lifeActual = (safeParse(sectionE.husbandLifeCover) || 0) + (safeParse(sectionE.wifeLifeCover) || 0);
        lifeTarget = Math.max(25000000, combinedAnnual * 10);
    }

    const coupleParams = [
        {
            label: "Combined Savings Rate",
            Icon: Coins,
            value: `${srVal.toFixed(1)}%`,
            subValue: `(₹${fmtK(investments)} of ₹${fmtL(combinedMonthly)})`,
            benchmark: ">20%",
            status: srVal >= 20 ? 'Excellent' : (srVal >= 10 ? 'Healthy' : '❌ Critical'),
            priority: srVal >= 20 ? 'LOW' : (srVal >= 10 ? 'MEDIUM' : 'URGENT'),
            grade: srVal >= 20 ? 'A' : (srVal >= 10 ? 'B' : 'F'),
            color: srVal >= 20 ? '#059669' : (srVal >= 10 ? '#d97706' : '#dc2626')
        },
        {
            label: "Essential Expenses Ratio",
            Icon: Wallet,
            value: `${eePct.toFixed(0)}%`,
            subValue: `(₹${fmtK(calcEssential)}/₹${fmtL(combinedMonthly)})`,
            benchmark: "<50%",
            status: eePct <= 50 ? 'Healthy' : (eePct <= 60 ? 'Manageable' : '❌ Critical'),
            priority: eePct <= 50 ? 'LOW' : (eePct <= 60 ? 'MEDIUM' : 'URGENT'),
            grade: eePct <= 50 ? 'A' : (eePct <= 60 ? 'B' : 'F'),
            color: eePct <= 50 ? '#059669' : (eePct <= 60 ? '#d97706' : '#dc2626')
        },
        {
            label: "Lifestyle Spending",
            Icon: ShoppingBag,
            value: `${lsPct.toFixed(0)}%`,
            subValue: `(₹${fmtK(lifestyleSpending)}/₹${fmtL(combinedMonthly)})`,
            benchmark: "10-15%",
            status: lsPct <= 15 ? 'Excellent' : (lsPct <= 20 ? 'Healthy' : '❌ Critical'),
            priority: lsPct <= 15 ? 'LOW' : (lsPct <= 20 ? 'MEDIUM' : 'URGENT'),
            grade: lsPct <= 15 ? 'A' : (lsPct <= 20 ? 'B' : 'F'),
            color: lsPct <= 15 ? '#059669' : (lsPct <= 20 ? '#d97706' : '#dc2626')
        },
        {
            label: "Joint Debt-to-Income (DTI)",
            Icon: Stats,
            value: `${dtiVal.toFixed(1)}%`,
            subValue: `(₹${fmtK(totalEMIs)}/₹${fmtL(combinedMonthly)})`,
            benchmark: "<35%",
            status: dtiVal === 0 ? 'Zero Debt' : (dtiVal <= 35 ? 'Manageable' : '❌ Critical'),
            priority: dtiVal === 0 ? 'LOW' : (dtiVal <= 35 ? 'MEDIUM' : 'URGENT'),
            grade: dtiVal === 0 ? 'A' : (dtiVal <= 35 ? 'B' : 'F'),
            color: dtiVal === 0 ? '#059669' : (dtiVal <= 35 ? '#d97706' : '#dc2626')
        },
        {
            label: "Joint Emergency Fund",
            Icon: Target,
            value: `₹${fmt(emActual)}`,
            subValue: "",
            benchmark: `₹${fmtL(emTarget)}\n(6 months)`,
            status: efPctFunded >= 100 ? 'Healthy' : (efPctFunded >= 50 ? 'Manageable' : '❌ Critical'),
            priority: efPctFunded >= 100 ? 'LOW' : (efPctFunded >= 50 ? 'MEDIUM' : 'URGENT'),
            grade: efPctFunded >= 100 ? 'A' : (efPctFunded >= 50 ? 'B' : 'F'),
            color: efPctFunded >= 100 ? '#059669' : (efPctFunded >= 50 ? '#d97706' : '#dc2626')
        },
        {
            label: "Joint Life Insurance",
            Icon: Heart,
            value: `₹${fmt(lifeActual)}`,
            subValue: "",
            benchmark: `₹${fmtL(lifeTarget)}`,
            status: (lifeActual / (lifeTarget || 1)) >= 1 ? 'Healthy' : ((lifeActual / (lifeTarget || 1)) >= 0.5 ? 'Inadequate' : '❌ Critical'),
            priority: (lifeActual / (lifeTarget || 1)) >= 1 ? 'LOW' : ((lifeActual / (lifeTarget || 1)) >= 0.5 ? 'MEDIUM' : 'CRITICAL'),
            grade: (lifeActual / (lifeTarget || 1)) >= 1 ? 'A' : ((lifeActual / (lifeTarget || 1)) >= 0.5 ? 'B' : 'F'),
            color: (lifeActual / (lifeTarget || 1)) >= 1 ? '#059669' : ((lifeActual / (lifeTarget || 1)) >= 0.5 ? '#d97706' : '#dc2626')
        }
    ];

    const params = isCouple ? coupleParams : [
        { key: 'sr', label: "Savings Rate", Icon: Coins },
        { key: 'eir', label: "Essential Expenses", Icon: Wallet },
        { key: 'lifestyle', label: "Lifestyle Spending", Icon: ShoppingBag },
        { key: 'dti', label: "Debt-to-Income", Icon: Stats },
        { key: 'ica', label: "Life Insurance", Icon: Heart },
        { key: 'efc', label: "Emergency Fund", Icon: Target },
    ];

    return (
        <div className="pdf-page diagnosis-page" style={{ height: '297mm', width: '210mm', position: 'relative', background: '#fff', padding: '120px 80px 80px' }}>
            <PageHeader sectionNumber={2} />
            
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', marginBottom: '10px', textTransform: 'uppercase' }}>SIX-PARAMETER COMPREHENSIVE DIAGNOSIS</h2>
            <p style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.6', marginBottom: '30px' }}>
                {name}, we measure financial health across six universal parameters. Think of this as your annual financial 
                health check-up — understanding where you stand against international benchmarks.
            </p>

            <table className="diagnosis-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                <thead>
                    <tr style={{ background: '#059669', color: '#fff', fontSize: '10px' }}>
                        <th style={{ padding: '10px', textAlign: 'left', borderRadius: '8px 0 0 0' }}>Parameter</th>
                        <th style={{ padding: '10px' }}>{isCouple ? "Combined Number" : "Your Number"}</th>
                        <th style={{ padding: '10px' }}>Benchmark</th>
                        <th style={{ padding: '10px' }}>Diagnosis</th>
                        <th style={{ padding: '10px', borderRadius: '0 8px 0 0', textAlign: 'right' }}>Priority</th>
                    </tr>
                </thead>
                <tbody>
                    {params.map((p, idx) => {
                        const item = isCouple ? p : (diagnosisData[p.key] || {});
                        const color = isCouple ? p.color : (item.grade === 'A' ? '#059669' : (item.grade === 'B' ? '#d97706' : '#dc2626'));
                        return (
                            <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ background: '#f0fdf4', padding: '6px', borderRadius: '8px' }}>
                                            <p.Icon color="#059669" size="18px" />
                                        </div>
                                        <div style={{ fontWeight: '700', color: '#059669', fontSize: '13px' }}>{p.label}</div>
                                    </div>
                                </td>
                                <td style={{ padding: '10px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '800', color: '#111827' }}>
                                        {isCouple ? p.value : (p.key === 'ica' ? `₹${(m.currentLifeCover || 0).toLocaleString()}` : item.value)}
                                        {isCouple && p.subValue && <div style={{ fontSize: '10px', color: '#334155', fontWeight: 'normal', marginTop: '2px' }}>{p.subValue}</div>}
                                    </div>
                                </td>
                                <td style={{ padding: '10px', textAlign: 'center', fontSize: '10px', fontWeight: '600', color: '#1e293b', whiteSpace: 'pre-line' }}>
                                    {isCouple ? p.benchmark : item.benchmark}
                                </td>
                                <td style={{ padding: '10px', textAlign: 'center' }}>
                                    <div style={{ color: color, fontWeight: '800', fontSize: '11px' }}>{item.status}</div>
                                </td>
                                <td style={{ padding: '10px', textAlign: 'right' }}>
                                    <div style={{ 
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        background: item.priority === 'LOW' ? '#f0fdf4' : (item.priority === 'MEDIUM' ? '#fffbeb' : '#fef2f2'),
                                        color: item.priority === 'LOW' ? '#166534' : (item.priority === 'MEDIUM' ? '#92400e' : '#991b1b'),
                                        fontWeight: '800', 
                                        fontSize: '10px' 
                                    }}>
                                        {item.priority}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <div style={{ marginTop: 'auto' }}>
                <div style={{ background: '#f8fafc', padding: '30px', borderRadius: '24px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
                        <div style={{ background: '#059669', color: '#fff', padding: '8px 20px', borderRadius: '12px', fontSize: '18px', fontWeight: '800' }}>
                            OVERALL SCORE {grades?.overall || 0}/100
                        </div>
                        <div style={{ flexGrow: 1, height: '10px', background: '#e2e8f0', borderRadius: '5px' }}>
                            <div style={{ width: `${grades?.overall || 0}%`, height: '100%', background: '#059669', borderRadius: '5px' }}></div>
                        </div>
                    </div>
                    <p style={{ fontSize: '13px', color: '#475569', lineHeight: '1.6' }}>
                        <strong>{grades?.overall || 0}/100 is an honest score.</strong> The gap is not years of sacrifice — 
                        it is a few smart decisions made today. By focusing on your red-priority areas, we can cross 
                        the 80/100 mark in less than 24 months.
                    </p>
                </div>
            </div>

            <PageFooter pageNumber={pageNum} clientName={name} />
        </div>
    );
};

export default DiagnosisPage;
